/**
 * The vocabulary of the portfolio: what kinds of property exist, what states a unit and a
 * tenancy move through, and how maintenance is triaged.
 *
 * Enumerations live here as data, not as TypeScript `enum`s, because every one of them is also
 * a `text` column with a CHECK constraint in Postgres and a dropdown in the UI. One list, three
 * consumers.
 */

import { normaliseChargeLabel, type ChargeType } from "./billing";
import { diffDays, type PlainDate } from "./dates";
import { applyRate, type BasisPoints, type Minor } from "./money";

/* ------------------------------------------------------------------ property */

export type PropertyType =
  | "block_of_flats"
  | "duplex"
  | "terrace"
  | "detached"
  | "semi_detached"
  | "bungalow"
  | "mini_flat"
  | "self_contained"
  | "shop"
  | "office"
  | "warehouse"
  | "event_centre"
  | "land"
  | "mixed_use";

export const PROPERTY_TYPES: Array<{ value: PropertyType; label: string; residential: boolean }> = [
  { value: "block_of_flats", label: "Block of flats", residential: true },
  { value: "duplex", label: "Duplex", residential: true },
  { value: "terrace", label: "Terrace", residential: true },
  { value: "semi_detached", label: "Semi-detached", residential: true },
  { value: "detached", label: "Detached house", residential: true },
  { value: "bungalow", label: "Bungalow", residential: true },
  // A room and a parlour, self-contained. The label is the whole of what it needs to say: everyone
  // letting or looking for one already knows what it is, and spelling it out on screen would be
  // explaining the market to the people who work in it.
  { value: "mini_flat", label: "Mini-flat", residential: true },
  { value: "self_contained", label: "Self-contained", residential: true },
  { value: "shop", label: "Shop / retail", residential: false },
  { value: "office", label: "Office", residential: false },
  { value: "warehouse", label: "Warehouse", residential: false },
  { value: "event_centre", label: "Event centre", residential: false },
  { value: "land", label: "Land", residential: false },
  { value: "mixed_use", label: "Mixed use", residential: false },
];

export function propertyTypeLabel(type: PropertyType): string {
  return PROPERTY_TYPES.find((t) => t.value === type)?.label ?? type;
}

/* ---------------------------------------------------------------------- unit */

export type UnitStatus = "vacant" | "occupied" | "notice" | "reserved" | "renovation" | "off_market";

export const UNIT_STATUSES: Array<{
  value: UnitStatus;
  label: string;
  tone: "success" | "warn" | "danger" | "muted";
  /** Counts towards occupancy in the dashboard. */
  occupied: boolean;
  /** Counts towards lettable stock — a unit under renovation is not a void the agent can fill. */
  lettable: boolean;
}> = [
  { value: "occupied", label: "Occupied", tone: "success", occupied: true, lettable: true },
  { value: "notice", label: "Notice given", tone: "warn", occupied: true, lettable: true },
  { value: "reserved", label: "Reserved", tone: "warn", occupied: false, lettable: true },
  { value: "vacant", label: "Vacant", tone: "danger", occupied: false, lettable: true },
  { value: "renovation", label: "Under renovation", tone: "muted", occupied: false, lettable: false },
  { value: "off_market", label: "Off market", tone: "muted", occupied: false, lettable: false },
];

/* ------------------------------------------------------------- what is on offer */

export type OfferType = "rent" | "lease" | "sale" | "short_let" | "joint_venture";

/**
 * What a unit is being offered as.
 *
 * Separate from its status, and the two answer different questions. **Status** is where the unit
 * is right now — occupied, vacant, under renovation. **Offer** is what the agency wants to do with
 * it, which is what somebody searching a listing is looking for. An occupied flat can be for sale
 * with the tenancy in place; a vacant one may be for rent and for sale at the same time while the
 * owner decides.
 *
 * So it is a set rather than a single value: "for sale or to let" is one of the commonest signs on
 * a building, and forcing a choice would make the listing lie about one of them.
 */
/**
 * The shape of the price each offer carries.
 *
 * Declared here rather than decided in the form, so the form, the listing card and the API all
 * agree about which price belongs to which offer. Asking for a monthly figure on a sale, or a sale
 * price on a short let, is the kind of mismatch that produces a listing nobody can act on.
 *
 * - `cycle` — an amount and how often it recurs. Rent and a lease differ in length, not in shape.
 * - `one_off` — a single price. A sale.
 * - `nightly` — a rate and whether it is per night or per week.
 * - `none` — a joint venture is negotiated; a number here would be an invention.
 */
export type OfferPricing = "cycle" | "one_off" | "nightly" | "none";

export const OFFER_TYPES: Array<{
  value: OfferType;
  label: string;
  /** The short word a listing card uses. */
  short: string;
  pricing: OfferPricing;
  /** What the form calls the amount for this offer. */
  priceLabel: string;
}> = [
  { value: "rent", label: "To rent", short: "Rent", pricing: "cycle", priceLabel: "Asking rent" },
  { value: "lease", label: "To lease", short: "Lease", pricing: "cycle", priceLabel: "Lease amount" },
  { value: "sale", label: "For sale", short: "Sale", pricing: "one_off", priceLabel: "Asking price" },
  { value: "short_let", label: "Short let", short: "Short let", pricing: "nightly", priceLabel: "Short-let rate" },
  {
    value: "joint_venture",
    label: "Joint venture",
    short: "Joint venture",
    pricing: "none",
    priceLabel: "Terms are negotiated",
  },
];

/** How a short let is quoted. Nightly is the common one; weekly is asked for often enough. */
export const SHORT_LET_PERIODS: Array<{ value: "night" | "week"; label: string }> = [
  { value: "night", label: "per night" },
  { value: "week", label: "per week" },
];

/**
 * Which offers in a set carry a price, in the order they should be shown.
 *
 * **One entry per offer, not per shape.** Rent and a lease both quote an amount and a cycle, but
 * they are not the same figure: a flat can be ₦3.5m a year to rent *and* ₦30m for a three-year
 * lease, and collapsing them into one set of fields would force the agency to publish whichever
 * they typed last. Each offer keeps its own price.
 */
export function pricingFor(offers: readonly string[] | null | undefined): Array<(typeof OFFER_TYPES)[number]> {
  const chosen = new Set(normaliseOffers(offers));
  return OFFER_TYPES.filter((offer) => chosen.has(offer.value) && offer.pricing !== "none");
}

export const DEFAULT_OFFER: OfferType = "rent";

/**
 * Clean a set of offers.
 *
 * Deduped, unknown values dropped, and **never empty** — a unit with no offer would vanish from
 * every filter, which reads as the unit having been deleted rather than as nobody having chosen.
 * Renting is what this product is mostly for, so that is what an unanswered question means.
 */
export function normaliseOffers(offers: readonly string[] | null | undefined): OfferType[] {
  const known = new Set(OFFER_TYPES.map((offer) => offer.value));
  const cleaned = (offers ?? []).filter((offer): offer is OfferType => known.has(offer as OfferType));
  const unique = [...new Set(cleaned)];
  return unique.length ? unique : [DEFAULT_OFFER];
}

/** "To rent and for sale" — the set said back in words. */
export function offerSummary(offers: readonly string[] | null | undefined): string {
  const labels = normaliseOffers(offers).map(
    (offer) => OFFER_TYPES.find((entry) => entry.value === offer)?.label ?? offer,
  );
  if (labels.length === 1) return labels[0];

  // Only the first keeps its capital: "To rent, for sale and short let" reads as one sentence,
  // where "To rent, For sale and Short let" reads as three headings jammed together.
  const [first, ...rest] = labels;
  const lowered = rest.map((label) => label.toLowerCase());
  const last = lowered[lowered.length - 1];
  return [first, ...lowered.slice(0, -1)].join(", ") + ` and ${last}`;
}

export function offerLabel(offer: string): string {
  return OFFER_TYPES.find((entry) => entry.value === offer)?.label ?? offer;
}

export function unitStatusInfo(status: UnitStatus) {
  return UNIT_STATUSES.find((s) => s.value === status) ?? UNIT_STATUSES[3];
}

export interface OccupancySummary {
  total: number;
  occupied: number;
  vacant: number;
  notice: number;
  unavailable: number;
  /** Occupied over lettable, as a whole percentage. */
  rate: number;
}

export function summariseOccupancy(units: Array<{ status: UnitStatus }>): OccupancySummary {
  const summary: OccupancySummary = { total: units.length, occupied: 0, vacant: 0, notice: 0, unavailable: 0, rate: 0 };
  let lettable = 0;
  for (const unit of units) {
    const info = unitStatusInfo(unit.status);
    if (info.lettable) lettable += 1;
    else summary.unavailable += 1;
    if (unit.status === "notice") summary.notice += 1;
    if (info.occupied) summary.occupied += 1;
    else if (info.lettable) summary.vacant += 1;
  }
  summary.rate = lettable === 0 ? 0 : Math.round((summary.occupied / lettable) * 100);
  return summary;
}

/* --------------------------------------------------------------------- lease */

export type LeaseStatus = "draft" | "active" | "expiring" | "expired" | "renewed" | "terminated" | "cancelled";

export const LEASE_STATUSES: Array<{ value: LeaseStatus; label: string; tone: "success" | "warn" | "danger" | "muted"; live: boolean }> = [
  { value: "draft", label: "Draft", tone: "muted", live: false },
  { value: "active", label: "Active", tone: "success", live: true },
  { value: "expiring", label: "Expiring", tone: "warn", live: true },
  { value: "expired", label: "Expired", tone: "danger", live: true },
  { value: "renewed", label: "Renewed", tone: "muted", live: false },
  { value: "terminated", label: "Terminated", tone: "muted", live: false },
  { value: "cancelled", label: "Cancelled", tone: "muted", live: false },
];

export function leaseStatusInfo(status: LeaseStatus) {
  return LEASE_STATUSES.find((s) => s.value === status) ?? LEASE_STATUSES[0];
}

/** How far ahead a live tenancy starts being called "expiring". */
export const EXPIRING_WINDOW_DAYS = 90;

/**
 * The status a tenancy should hold today. Run nightly by `leases.roll`, and also used on read so
 * a screen never shows "active" on something that quietly expired at midnight.
 */
export function deriveLeaseStatus(
  lease: { status: LeaseStatus; startDate: PlainDate; endDate: PlainDate | null },
  asOf: PlainDate,
): LeaseStatus {
  if (lease.status === "draft" || lease.status === "renewed" || lease.status === "terminated" || lease.status === "cancelled") {
    return lease.status;
  }
  if (!lease.endDate) return "active";
  const days = diffDays(asOf, lease.endDate);
  if (days < 0) return "expired";
  if (days <= EXPIRING_WINDOW_DAYS) return "expiring";
  return "active";
}

/* --------------------------------------------------------------- maintenance */

export type MaintenancePriority = "emergency" | "high" | "normal" | "low";

export const MAINTENANCE_PRIORITIES: Array<{
  value: MaintenancePriority;
  label: string;
  /** Target response, in hours. Breaching it puts the job on the dashboard action queue. */
  slaHours: number;
  tone: "danger" | "warn" | "info" | "muted";
}> = [
  { value: "emergency", label: "Emergency", slaHours: 4, tone: "danger" },
  { value: "high", label: "High", slaHours: 24, tone: "warn" },
  { value: "normal", label: "Normal", slaHours: 72, tone: "info" },
  { value: "low", label: "Low", slaHours: 168, tone: "muted" },
];

export function slaHoursFor(priority: MaintenancePriority): number {
  return MAINTENANCE_PRIORITIES.find((p) => p.value === priority)?.slaHours ?? 72;
}

export type MaintenanceStatus = "open" | "assigned" | "in_progress" | "on_hold" | "resolved" | "closed" | "rejected";

export const MAINTENANCE_STATUSES: Array<{ value: MaintenanceStatus; label: string; open: boolean }> = [
  { value: "open", label: "Open", open: true },
  { value: "assigned", label: "Assigned", open: true },
  { value: "in_progress", label: "In progress", open: true },
  { value: "on_hold", label: "On hold", open: true },
  { value: "resolved", label: "Resolved", open: false },
  { value: "closed", label: "Closed", open: false },
  { value: "rejected", label: "Rejected", open: false },
];

export const MAINTENANCE_CATEGORIES = [
  "Plumbing",
  "Electrical",
  "Roofing",
  "Structural",
  "Painting",
  "Doors and windows",
  "Appliances",
  "Pest control",
  "Security",
  "Water supply",
  "Power / generator",
  "Waste",
  "Cleaning",
  "Other",
] as const;

/* ------------------------------------------------------------------- parties */

export type PartyRole = "tenant" | "landlord" | "guarantor" | "vendor" | "buyer" | "other";

export const PARTY_ROLES: Array<{ value: PartyRole; label: string; plural: string }> = [
  { value: "tenant", label: "Tenant", plural: "Tenants" },
  { value: "landlord", label: "Landlord", plural: "Landlords" },
  { value: "guarantor", label: "Guarantor", plural: "Guarantors" },
  { value: "vendor", label: "Vendor / artisan", plural: "Vendors" },
  { value: "buyer", label: "Buyer", plural: "Buyers" },
  { value: "other", label: "Other", plural: "Others" },
];

/* -------------------------------------------------------------------- phones */

export interface PartyPhone {
  number: string;
  /** Whether this number is on WhatsApp. Several can be. */
  whatsapp: boolean;
}

/**
 * Clean a list of numbers.
 *
 * Blank rows dropped, duplicates dropped keeping the first, and the order preserved — the order is
 * the answer to "which one do I ring first", so re-sorting it would throw away what the person
 * told us.
 */
export function normalisePhones(phones: readonly Partial<PartyPhone>[] | null | undefined): PartyPhone[] {
  const seen = new Set<string>();
  const out: PartyPhone[] = [];

  for (const entry of phones ?? []) {
    const number = String(entry?.number ?? "").trim();
    if (!number) continue;
    const key = number.replace(/\D/g, "");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ number, whatsapp: Boolean(entry?.whatsapp) });
  }
  return out;
}

/** The number to ring first — the one a receipt prints and a duplicate check compares. */
export function primaryPhone(phones: readonly Partial<PartyPhone>[] | null | undefined): string | null {
  return normalisePhones(phones)[0]?.number ?? null;
}

/** Every number that can take a WhatsApp message, in the order they were given. */
export function whatsappNumbers(phones: readonly Partial<PartyPhone>[] | null | undefined): string[] {
  return normalisePhones(phones)
    .filter((entry) => entry.whatsapp)
    .map((entry) => entry.number);
}

/**
 * The one a bulk send uses.
 *
 * The first flagged for WhatsApp. A send to one person offers the choice; a send to two hundred
 * cannot ask two hundred questions, so it takes the first and is predictable about it.
 */
export function bulkWhatsappNumber(phones: readonly Partial<PartyPhone>[] | null | undefined): string | null {
  return whatsappNumbers(phones)[0] ?? null;
}

/* ------------------------------------------------------------------ payments */

export type PaymentMethod = "cash" | "bank_transfer" | "cheque" | "pos" | "card" | "mobile_money" | "online" | "offset";

export const PAYMENT_METHODS: Array<{ value: PaymentMethod; label: string; needsReference: boolean }> = [
  { value: "bank_transfer", label: "Bank transfer", needsReference: true },
  { value: "cash", label: "Cash", needsReference: false },
  { value: "pos", label: "POS", needsReference: true },
  { value: "mobile_money", label: "Mobile money", needsReference: true },
  { value: "cheque", label: "Cheque", needsReference: true },
  { value: "card", label: "Card", needsReference: true },
  { value: "online", label: "Online payment", needsReference: true },
  { value: "offset", label: "Offset / credit note", needsReference: false },
];

export function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHODS.find((m) => m.value === method)?.label ?? method;
}

export const EXPENSE_CATEGORIES = [
  "Repairs and maintenance",
  "Utilities",
  "Security",
  "Cleaning and waste",
  "Insurance",
  "Rates and levies",
  "Agency commission",
  "Legal and professional",
  "Renovation",
  "Furnishing",
  "Other",
] as const;

/* ------------------------------------------------------ more than one type */

/**
 * A property is a *set* of types, not one.
 *
 * A block with shops on the ground floor is a block of flats **and** retail; an estate can hold
 * houses and a small parade. Forcing one label meant choosing which half of the building to lie
 * about, and "mixed use" only named the problem rather than describing what is actually there.
 *
 * **The first entry is the primary.** It draws the mark in every list and decides how the
 * property sorts, because a row has space for one icon. The rest are shown on the property itself
 * and are what reports and filters match against.
 */
export function primaryType(types: readonly string[] | null | undefined): PropertyType {
  const first = types?.[0];
  return (first as PropertyType) ?? "block_of_flats";
}

/** Normalise whatever arrives: no blanks, no duplicates, never empty. */
export function normaliseTypes(types: readonly string[] | null | undefined): PropertyType[] {
  const known = new Set(PROPERTY_TYPES.map((t) => t.value));
  const out: PropertyType[] = [];
  for (const value of types ?? []) {
    const type = value as PropertyType;
    if (known.has(type) && !out.includes(type)) out.push(type);
  }
  return out.length ? out : ["block_of_flats"];
}

/** "Block of flats and shop / retail" — for a heading or a detail row. */
export function propertyTypeSummary(types: readonly string[] | null | undefined): string {
  const labels = normaliseTypes(types).map((type) => propertyTypeLabel(type));
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * Types that are one dwelling by nature.
 *
 * A detached house, a bungalow or a self-contained flat is almost always let whole. When one of
 * these is chosen the form offers to create the single unit alongside the property, because
 * making somebody add a "property" and then add its one "unit" is a second form for no
 * information — the unit's only distinguishing fact is that there is one of it.
 */
export const SINGLE_UNIT_TYPES: PropertyType[] = ["detached", "bungalow", "self_contained", "duplex", "land"];

export function looksLikeOneUnit(types: readonly string[] | null | undefined): boolean {
  const list = normaliseTypes(types);
  return list.length === 1 && SINGLE_UNIT_TYPES.includes(list[0]);
}

/* ------------------------------------------------------------- what it has */

/**
 * Amenities — the things a listing is judged on before anybody reads the description.
 *
 * **Ordered by how often they decide a viewing, not alphabetically.** A borehole, a generator and
 * a gated street are the first three questions asked about a flat in this market; a cinema room is
 * the twentieth. The form shows the common ones first and folds the rest away, because a wall of
 * thirty tick boxes gets skipped and then the listing has none.
 *
 * **`common` is a claim about this market, not about the product.** It is the only thing to change
 * when that turns out to be wrong, and every screen reads the order from here.
 *
 * The vocabulary is deliberately the one used on a sign outside a building — "Boys' quarters",
 * "Prepaid meter" — rather than a normalised taxonomy. A listing is read by somebody who is
 * looking for a flat, not by a database.
 */
export const AMENITIES: Array<{ value: string; label: string; common?: boolean }> = [
  { value: "borehole", label: "Borehole / water supply", common: true },
  { value: "generator", label: "Generator / back-up power", common: true },
  { value: "security", label: "24-hour security", common: true },
  { value: "gated", label: "Gated estate", common: true },
  { value: "parking", label: "Parking space", common: true },
  { value: "boys_quarters", label: "Boys' quarters", common: true },
  { value: "en_suite", label: "All rooms en-suite", common: true },
  { value: "fitted_kitchen", label: "Fitted kitchen", common: true },
  { value: "wardrobes", label: "Fitted wardrobes", common: true },
  { value: "prepaid_meter", label: "Prepaid meter", common: true },
  { value: "air_conditioning", label: "Air conditioning", common: true },
  { value: "furnished", label: "Furnished", common: true },
  { value: "serviced", label: "Serviced", common: true },
  { value: "newly_built", label: "Newly built", common: true },
  { value: "pop_ceiling", label: "POP ceiling" },
  { value: "tiled", label: "Tiled throughout" },
  { value: "water_heater", label: "Water heater" },
  { value: "water_treatment", label: "Water treatment" },
  { value: "balcony", label: "Balcony" },
  { value: "garden", label: "Garden" },
  { value: "swimming_pool", label: "Swimming pool" },
  { value: "gym", label: "Gym" },
  { value: "cctv", label: "CCTV" },
  { value: "lift", label: "Lift" },
  { value: "solar", label: "Solar power" },
  { value: "study", label: "Study / home office" },
  { value: "cinema", label: "Cinema room" },
  { value: "wifi", label: "Internet / wifi" },
  { value: "pet_friendly", label: "Pets allowed" },
  { value: "wheelchair", label: "Step-free access" },
];

export type Amenity = (typeof AMENITIES)[number]["value"];

/** The ones a form shows before it offers to show the rest. */
export const COMMON_AMENITIES = AMENITIES.filter((entry) => entry.common);

export const amenityLabel = (value: string): string =>
  AMENITIES.find((entry) => entry.value === value)?.label ?? value;

/**
 * Whatever arrived, as a clean set in the published order.
 *
 * Unknown values are dropped rather than kept: they would show on a public listing as a raw
 * database string, and a listing that says `pop_ceiling` reads as a mistake about the whole site.
 * Empty is a perfectly good answer here — unlike offers, a unit with no amenities is ordinary.
 */
export function normaliseAmenities(amenities: readonly string[] | null | undefined): string[] {
  const known = new Set(AMENITIES.map((entry) => entry.value));
  const chosen = new Set((amenities ?? []).filter((value) => known.has(value)));
  return AMENITIES.filter((entry) => chosen.has(entry.value)).map((entry) => entry.value);
}

/* -------------------------------------------- what is payable besides the price */

/**
 * How a charge is quoted.
 *
 * Both are asked for constantly and neither can stand in for the other. A caution deposit is
 * quoted as a figure; an agency fee is quoted as "ten per cent", and the whole point of storing it
 * that way is that it follows the price — put the asking price up and the fee is right without
 * anybody remembering to change it.
 */
export type ChargeBasis = "fixed" | "percent";

/**
 * Something payable on top of the price, tied to the offer it belongs to.
 *
 * **The offer is the point.** A unit can be to let *and* for sale at once — that is one of the
 * commonest signs on a building here, and the prices are already kept apart for it. The charges
 * have to be kept apart the same way: a caution deposit and a service charge belong to the tenancy;
 * an agency fee of five per cent of ₦180m belongs to the sale. Without the `offer` field the sale
 * would advertise a deposit and the tenancy would advertise a commission on a price nobody is
 * paying.
 *
 * **A percentage is of that offer's own price**, never of the other's. Five per cent of the sale
 * price and five per cent of the annual rent are different numbers by two orders of magnitude, and
 * resolving against the wrong one is the sort of error that ends in a refund.
 *
 * `value` is minor units when `fixed` and basis points when `percent` — the same convention
 * `LeaseCharge` uses, so a unit's charges can seed a tenancy's without conversion.
 */
export interface UnitCharge {
  offer: OfferType;
  type: ChargeType;
  /** Only for `other`, where the type does not name it. */
  label?: string;
  basis: ChargeBasis;
  /** Minor units when `fixed`, basis points when `percent`. */
  value: number;
  /**
   * Whose money this charge is — a `beneficiaries` row belonging to the agency.
   *
   * **It is never published.** This is the shape stored on a unit and read by the public listing
   * site's price breakdown, and the breakdown is read by strangers: a beneficiary is a landlord as
   * often as it is a lawyer, so naming one there would print the owner's name against their own
   * address on a page anybody can find. `shape()` in `routes/market.ts` sends the label, the rate
   * and the money and nothing about the party; `tools/verify-listings.mjs` asserts the absence
   * rather than leaving it to be noticed.
   *
   * What it is for is the other end: a unit's asking charges seed a tenancy's, and without this
   * field the answer to "whose is the legal fee on this letting" was thrown away every time a
   * manager picked a unit and had to be given again. It is optional because every charge stored
   * before beneficiaries existed has none, and because a unit advertised without one still lets —
   * `withBeneficiaries()` in `routes/leases.ts` supplies the default at the moment the tenancy is
   * recorded, which is the last point at which an answer can still be wrong and cheap to correct.
   */
  beneficiaryId?: string | null;
}

/**
 * What may be charged against each offer.
 *
 * Not every charge makes sense against every offer, and offering all of them is how a sale ends up
 * advertising a caution deposit. A joint venture is absent entirely: it has no price, so there is
 * nothing for a percentage to be a percentage of and nothing to add a fee to.
 */
export const CHARGES_ALLOWED: Record<OfferType, ChargeType[]> = {
  rent: ["service_charge", "deposit", "agency_fee", "legal_fee", "other"],
  lease: ["service_charge", "deposit", "agency_fee", "legal_fee", "other"],
  sale: ["agency_fee", "legal_fee", "other"],
  short_let: ["service_charge", "deposit", "other"],
  joint_venture: [],
};

/** Whether this offer can carry charges at all — everything with a price can. */
export const chargeableOffer = (offer: OfferType): boolean => CHARGES_ALLOWED[offer]?.length > 0;

/**
 * Clean a set of charges.
 *
 * Unknown offers, charges an offer cannot carry, negative figures and percentages over 100 are
 * dropped rather than corrected: a charge nobody can explain is worse on a listing than no charge
 * at all. Charges whose offer is not among the ones chosen go too, so unticking "for sale" takes
 * its commission with it instead of leaving it to reappear when the offer is ticked again.
 *
 * **The name on an `other` charge is trimmed, capped and confined to `other`.** It is the one
 * charge type that does not say what it is, so the agency names it — but a name left behind on a
 * charge whose type has since changed would rename a caution deposit on an invoice a tenant keeps.
 * `normaliseChargeLabel` in `billing.ts` is the single statement of that rule; the display side
 * goes through `chargeLabel` for the same reason, because rows outlive the code that wrote them.
 */
export function normaliseCharges(
  charges: readonly UnitCharge[] | null | undefined,
  offers?: readonly string[] | null,
): UnitCharge[] {
  const chosen = offers === undefined ? null : new Set(normaliseOffers(offers));
  return (charges ?? [])
    .filter((charge) => {
      if (!charge || typeof charge.value !== "number" || !Number.isInteger(charge.value)) return false;
      if (charge.value <= 0) return false;
      if (charge.basis !== "fixed" && charge.basis !== "percent") return false;
      if (charge.basis === "percent" && charge.value > 10_000) return false;
      if (!CHARGES_ALLOWED[charge.offer]?.includes(charge.type)) return false;
      if (chosen && !chosen.has(charge.offer)) return false;
      return true;
    })
    .map(({ label: given, beneficiaryId: to, ...rest }) => {
      const label = normaliseChargeLabel(rest.type, given);
      /* Whose money it is, kept only when it is actually an id. A `null`, an empty string and a
         number all mean "nobody has said", and they must all be stored the same way — as no key at
         all — or three readers each grow their own idea of what an unanswered question looks like.
         The id is not checked for existence here: this is a pure function and ownership is the
         API's to prove, which `routes/portfolio.ts` does through `ownedMany()` before it calls. */
      const beneficiaryId = typeof to === "string" && to.trim() ? to.trim() : undefined;
      /* The keys are left off entirely rather than set to undefined, so what is stored for a charge
         nobody named and nobody attributed is byte for byte what was stored before either field
         existed. */
      const named = label === undefined ? rest : { ...rest, label };
      return beneficiaryId === undefined ? named : { ...named, beneficiaryId };
    });
}

/** The charges attached to one offer, in the order the charge types are declared. */
export function chargesFor(charges: readonly UnitCharge[] | null | undefined, offer: OfferType): UnitCharge[] {
  const order = CHARGES_ALLOWED[offer] ?? [];
  return (charges ?? [])
    .filter((charge) => charge.offer === offer)
    .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
}

/**
 * What one charge comes to against a price.
 *
 * Named for the unit rather than called `resolveCharge`, because `billing.ts` already has that
 * name for the lease-side version. They are deliberately separate: this one answers "what does the
 * advert say", that one answers "what goes on the invoice".
 *
 * Returns `null` for a percentage with no price behind it — an unpriced offer cannot say what ten
 * per cent of it is, and showing `0` there would read as "nothing to pay".
 */
export function unitChargeAmount(charge: UnitCharge, price: Minor | null | undefined): Minor | null {
  if (charge.basis === "fixed") return charge.value;
  if (price == null) return null;
  return applyRate(price, charge.value as BasisPoints);
}

/** Everything payable on top of one offer's price, or `null` if any part of it cannot be worked out. */
export function unitChargeTotal(
  charges: readonly UnitCharge[] | null | undefined,
  offer: OfferType,
  price: Minor | null | undefined,
): Minor | null {
  const resolved = chargesFor(charges, offer).map((charge) => unitChargeAmount(charge, price));
  if (resolved.some((amount) => amount === null)) return null;
  return resolved.reduce((total: Minor, amount) => total + (amount ?? 0), 0);
}

/* --------------------------------------------------------------- public links */

/**
 * The address a unit has on the public site.
 *
 * `/listing/flat-3b-palm-court-ikoyi-01M0G…` reads as a place in a search result and in a WhatsApp
 * message, where `/listing/01M0G…` reads as nothing at all. **Only the id is looked up** — it is
 * the last segment, and everything before it is prose for people. That is what lets the agency app
 * build the same link from what a list row happens to know, without asking the server for it.
 *
 * It lives here rather than in the marketplace routes because two apps need it now: the site that
 * serves the page, and the application that links to it.
 */
export function listingSlug(row: { id: string; label: string; propertyName: string; city?: string | null }): string {
  const words = [row.label, row.propertyName, row.city]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 70);
  return `${words}-${row.id}`;
}

/** The id is the last segment; everything before it is prose. */
export const idFromListingSlug = (slug: string): string => slug.slice(slug.lastIndexOf("-") + 1);
